import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { crypto as cv, type dto } from '@photo-vault/shared';
import {
  AlbumPickerCombobox,
  Button,
  Card,
  Icon,
  Input,
  PageEyebrow,
  type AlbumPickerOption,
} from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from './albumKeys.js';
import { AlbumPhotosSection } from './AlbumPhotosSection.js';
import { ShareSection } from '../share/ShareSection.js';
import { RotationSection } from '../rotation/RotationSection.js';
import { forgetAlbumKey } from './albumKeys.js';

interface DecryptedNode {
  album: dto.Album;
  name: string;
}

interface LoadedTree {
  rootId: string;
  current: dto.Album;
  albumKey: CryptoKey;
  byId: Map<string, DecryptedNode>;
  childrenByParent: Map<string, DecryptedNode[]>;
}

// Walk parents to build a breadcrumb of names (root → leaf, excluding the
// node itself). Used by the AlbumPickerCombobox to disambiguate same-named
// sub-albums under different branches.
function breadcrumbFor(byId: Map<string, DecryptedNode>, leafId: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let cursor: DecryptedNode | undefined = byId.get(leafId);
  // Skip the leaf itself.
  if (cursor) cursor = cursor.album.parentId ? byId.get(cursor.album.parentId) : undefined;
  while (cursor && !seen.has(cursor.album.id)) {
    seen.add(cursor.album.id);
    out.unshift(cursor.name);
    cursor = cursor.album.parentId ? byId.get(cursor.album.parentId) : undefined;
  }
  return out;
}

export function AlbumDetailPage() {
  const { albumId } = useParams<{ albumId: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadedTree | 'loading' | { error: string }>('loading');
  const [creatingChild, setCreatingChild] = useState(false);
  const [childName, setChildName] = useState('');
  const [childError, setChildError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [albumMoveDestId, setAlbumMoveDestId] = useState<string | null>(null);

  async function reload() {
    if (!albumId) return;
    setState('loading');
    try {
      const { albums } = await metadata.listAlbums(session.jwt);
      const self = albums.find((a) => a.id === albumId);
      if (!self) {
        setState({ error: 'album non trovato' });
        return;
      }
      const root = albums.find((a) => a.id === self.rootAlbumId);
      if (!root || !root.wrappedAlbumKey) {
        setState({ error: 'chiave radice mancante' });
        return;
      }
      const albumKey = await getAlbumKey(root.rootAlbumId, root.wrappedAlbumKey, session.rsaPriv);

      const { nodes } = await metadata.fetchAlbumTree(self.rootAlbumId, session.jwt);
      const decrypted: DecryptedNode[] = await Promise.all(
        nodes.map(async (album) => {
          try {
            const name = await cv.decryptText(album.encryptedName, albumKey);
            return { album, name };
          } catch {
            return { album, name: '(decrittura fallita)' };
          }
        }),
      );

      const byId = new Map<string, DecryptedNode>();
      const childrenByParent = new Map<string, DecryptedNode[]>();
      for (const node of decrypted) {
        byId.set(node.album.id, node);
        const parentKey = node.album.parentId ?? '';
        const arr = childrenByParent.get(parentKey) ?? [];
        arr.push(node);
        childrenByParent.set(parentKey, arr);
      }
      for (const arr of childrenByParent.values()) {
        arr.sort((a, b) => a.album.createdAt.localeCompare(b.album.createdAt));
      }

      setState({ rootId: self.rootAlbumId, current: self, albumKey, byId, childrenByParent });
      setRenameValue(byId.get(albumId)?.name ?? '');
      setAlbumMoveDestId(null);
    } catch (err) {
      setState({ error: err instanceof Error ? err.message : 'errore' });
    }
  }

  useEffect(() => {
    void reload();
  }, [albumId, session.jwt]);

  const breadcrumb = useMemo(() => {
    if (typeof state !== 'object' || 'error' in state) return [];
    const out: DecryptedNode[] = [];
    let cursor: dto.Album | undefined = state.current;
    while (cursor) {
      const node = state.byId.get(cursor.id);
      if (!node) break;
      out.unshift(node);
      if (cursor.parentId === null) break;
      cursor = state.byId.get(cursor.parentId)?.album;
    }
    return out;
  }, [state]);

  const albumMoveOptions: AlbumPickerOption[] = useMemo(() => {
    if (typeof state !== 'object' || 'error' in state) return [];
    if (state.current.parentId === null) return [];
    // Forbidden destinations: the album itself, anything in its subtree
    // (would create a cycle), and the current parent (no-op).
    const forbidden = new Set<string>();
    const stack = [state.current.id];
    while (stack.length > 0) {
      const id = stack.pop() as string;
      forbidden.add(id);
      const kids = state.childrenByParent.get(id) ?? [];
      for (const k of kids) stack.push(k.album.id);
    }
    forbidden.add(state.current.parentId);
    return [...state.byId.values()]
      .filter((n) => !forbidden.has(n.album.id))
      .map((n) => ({
        id: n.album.id,
        name: n.name,
        breadcrumb: breadcrumbFor(state.byId, n.album.id),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [state]);

  const photoMoveOptions: AlbumPickerOption[] = useMemo(() => {
    if (typeof state !== 'object' || 'error' in state) return [];
    // Photo move: any node in the same root, excluding the current album
    // (where the photos already live). Cross-root is rejected by the API
    // anyway (would require re-encryption).
    return [...state.byId.values()]
      .filter((n) => n.album.id !== state.current.id)
      .map((n) => ({
        id: n.album.id,
        name: n.name,
        breadcrumb: breadcrumbFor(state.byId, n.album.id),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [state]);

  if (!albumId) return null;
  if (state === 'loading') return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  if ('error' in state) return <p className="py-6 text-sm text-error">Errore: {state.error}</p>;

  const currentNode = state.byId.get(state.current.id);
  const children = state.childrenByParent.get(state.current.id) ?? [];

  async function handleCreateChild(e: FormEvent) {
    e.preventDefault();
    setChildError(null);
    const name = childName.trim();
    if (!name) {
      setChildError('inserisci un nome');
      return;
    }
    if (typeof state !== 'object' || 'error' in state) return;
    setCreatingChild(true);
    try {
      const root = state.byId.get(state.rootId)?.album;
      if (!root?.wrappedAlbumKey) throw new Error('chiave radice mancante');
      const albumKey = await getAlbumKey(state.rootId, root.wrappedAlbumKey, session.rsaPriv);
      const encryptedName = await cv.encryptText(name, albumKey);
      await metadata.createChildAlbum({ parentId: state.current.id, encryptedName }, session.jwt);
      setChildName('');
      await reload();
    } catch (err) {
      setChildError(err instanceof Error ? err.message : 'errore');
    } finally {
      setCreatingChild(false);
    }
  }

  async function handleRename(e: FormEvent) {
    e.preventDefault();
    setRenameError(null);
    const name = renameValue.trim();
    if (!name) {
      setRenameError('inserisci un nome');
      return;
    }
    if (typeof state !== 'object' || 'error' in state) return;
    setRenaming(true);
    try {
      const root = state.byId.get(state.rootId)?.album;
      if (!root?.wrappedAlbumKey) throw new Error('chiave radice mancante');
      const albumKey = await getAlbumKey(state.rootId, root.wrappedAlbumKey, session.rsaPriv);
      const encryptedName = await cv.encryptText(name, albumKey);
      await metadata.patchAlbum(state.current.id, { encryptedName }, session.jwt);
      await reload();
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : 'errore');
    } finally {
      setRenaming(false);
    }
  }

  async function handleMoveAlbum() {
    if (typeof state !== 'object' || 'error' in state) return;
    if (!albumMoveDestId) return;
    setBusyId(state.current.id);
    try {
      await metadata.patchAlbum(state.current.id, { parentId: albumMoveDestId }, session.jwt);
      await reload();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete() {
    if (typeof state !== 'object' || 'error' in state) return;
    const subtree = state.childrenByParent.get(state.current.id) ?? [];
    const isRoot = state.current.parentId === null;
    const tail =
      ' Verranno eliminate anche tutte le foto contenute (i blob cifrati saranno rimossi dal bucket).';
    const msg = isRoot
      ? `Eliminare l'album radice "${currentNode?.name}" e tutti i suoi sotto-album?${tail}`
      : `Eliminare "${currentNode?.name}"${subtree.length > 0 ? ` e ${subtree.length} sotto-album` : ''}?${tail}`;
    if (!window.confirm(msg)) return;
    setBusyId(state.current.id);
    try {
      await metadata.deleteAlbum(state.current.id, session.jwt);
      navigate(isRoot ? '/albums' : `/albums/${state.current.parentId}`, { replace: true });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
      setBusyId(null);
    }
  }

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-3">
        <PageEyebrow>
          {state.current.parentId === null ? 'album radice' : 'sotto-album'}
        </PageEyebrow>
        <Breadcrumb nodes={breadcrumb} />
      </div>

      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Icon name="drive_file_rename_outline" className="text-2xl text-cyan" />
          <h3 className="font-display text-lg font-semibold">Rinomina</h3>
        </div>
        <form onSubmit={handleRename} className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <Input
              label="nome"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              {...(renameError ? { error: renameError } : {})}
              disabled={renaming}
            />
          </div>
          <Button type="submit" loading={renaming} leftIcon={<Icon name="save" />}>
            Salva
          </Button>
        </form>
      </Card>

      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Icon name="create_new_folder" className="text-2xl text-cyan" />
          <h3 className="font-display text-lg font-semibold">Nuovo sotto-album</h3>
        </div>
        <form onSubmit={handleCreateChild} className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <Input
              label="nome"
              placeholder="es. Mare"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              {...(childError ? { error: childError } : {})}
              disabled={creatingChild}
            />
          </div>
          <Button type="submit" loading={creatingChild} leftIcon={<Icon name="add" />}>
            Crea
          </Button>
        </form>
      </Card>

      <div className="flex flex-col gap-3">
        <h3 className="font-display text-lg font-semibold">Sotto-album</h3>
        {children.length === 0 ? (
          <Card className="flex flex-col items-center gap-2 py-8 text-center">
            <Icon name="folder_open" className="text-3xl text-on-surface-variant" />
            <p className="text-sm text-on-surface-variant">Nessun sotto-album.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {children.map((child) => (
              <Link key={child.album.id} to={`/albums/${child.album.id}`} className="block">
                <Card className="flex flex-col gap-3 transition-colors hover:border-cyan/60">
                  <div className="flex items-center gap-3">
                    <Icon name="folder" className="text-2xl text-cyan" />
                    <h4 className="flex-1 truncate font-display text-base font-semibold">
                      {child.name}
                    </h4>
                    <Icon name="arrow_forward" className="text-base text-on-surface-variant" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      <AlbumPhotosSection
        albumId={state.current.id}
        rootAlbumId={state.rootId}
        albumKey={state.albumKey}
        movePickerOptions={photoMoveOptions}
      />

      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Icon name="sell" className="text-2xl text-cyan" />
          <h3 className="font-display text-lg font-semibold">Tag</h3>
        </div>
        <p className="text-xs text-on-surface-variant">
          Etichette cifrate trasversali ai sotto-album della stessa radice. Crea, rinomina o
          cancella un tag, oppure apri una foto per applicarlo.
        </p>
        <Link to={`/albums/${encodeURIComponent(state.current.id)}/tags`} className="self-start">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-cyan transition-colors hover:underline">
            Apri elenco tag
            <Icon name="arrow_forward" className="text-base" />
          </span>
        </Link>
      </Card>

      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Icon name="star" filled className="text-2xl text-warning" />
          <h3 className="font-display text-lg font-semibold">Più valutate</h3>
        </div>
        <p className="text-xs text-on-surface-variant">
          Solo le foto valutate, ordinate per stelle attraverso ogni sotto-album della radice.
        </p>
        <Link
          to={`/albums/${encodeURIComponent(state.current.id)}/top-rated`}
          className="self-start"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-cyan transition-colors hover:underline">
            Apri classifica
            <Icon name="arrow_forward" className="text-base" />
          </span>
        </Link>
      </Card>

      {state.current.parentId === null ? (
        <>
          <Card className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Icon name="content_copy" className="text-2xl text-cyan" />
              <h3 className="font-display text-lg font-semibold">Duplicati</h3>
            </div>
            <p className="text-xs text-on-surface-variant">
              Trova foto byte-identiche distribuite tra i sotto-album. La scansione resta sul tuo
              dispositivo: nessun hash del contenuto viene inviato al server.
            </p>
            <Link
              to={`/albums/${encodeURIComponent(state.rootId)}/duplicates`}
              className="self-start"
            >
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-cyan transition-colors hover:underline">
                Apri scansione duplicati
                <Icon name="arrow_forward" className="text-base" />
              </span>
            </Link>
          </Card>
          <ShareSection rootAlbumId={state.rootId} albumKey={state.albumKey} />
          <RotationSection
            rootAlbumId={state.rootId}
            currentAlbumKey={state.albumKey}
            onRotated={() => {
              forgetAlbumKey(state.rootId);
              void reload();
            }}
          />
        </>
      ) : null}

      {state.current.parentId !== null && albumMoveOptions.length > 0 ? (
        <Card className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Icon name="drive_file_move" className="text-2xl text-cyan" />
            <h3 className="font-display text-lg font-semibold">Sposta in…</h3>
          </div>
          <p className="text-xs text-on-surface-variant">
            Solo nodi all'interno dello stesso albero radice.
          </p>
          <div className="flex flex-col gap-3 md:flex-row md:items-start">
            <div className="flex-1">
              <AlbumPickerCombobox
                options={albumMoveOptions}
                value={albumMoveDestId}
                onChange={setAlbumMoveDestId}
                placeholder="Cerca un album di destinazione…"
                disabled={busyId === state.current.id}
              />
            </div>
            <Button
              variant="primary"
              onClick={handleMoveAlbum}
              disabled={!albumMoveDestId}
              loading={busyId === state.current.id}
              leftIcon={<Icon name="drive_file_move" />}
            >
              Sposta qui
            </Button>
          </div>
        </Card>
      ) : null}

      <Card className="flex flex-col gap-3 border-error/40">
        <div className="flex items-center gap-3">
          <Icon name="delete" className="text-2xl text-error" />
          <h3 className="font-display text-lg font-semibold">Elimina</h3>
        </div>
        <p className="text-xs text-on-surface-variant">
          Cancella questo nodo, tutti i suoi sotto-album e ogni foto contenuta. I blob cifrati
          vengono rimossi dal tuo bucket S3. L'operazione è irreversibile.
        </p>
        <Button
          variant="danger"
          loading={busyId === state.current.id}
          onClick={handleDelete}
          leftIcon={<Icon name="delete_forever" />}
          className="self-start"
        >
          Elimina
        </Button>
      </Card>
    </div>
  );
}

function Breadcrumb({ nodes }: { nodes: DecryptedNode[] }) {
  return (
    <nav className="flex flex-wrap items-center gap-2 text-sm">
      <Link to="/albums" className="text-on-surface-variant transition-colors hover:text-cyan">
        Album
      </Link>
      {nodes.map((node, idx) => (
        <span key={node.album.id} className="flex items-center gap-2">
          <Icon name="chevron_right" className="text-base text-on-surface-variant" />
          {idx === nodes.length - 1 ? (
            <span className="font-display text-base font-semibold text-cyan">{node.name}</span>
          ) : (
            <Link
              to={`/albums/${node.album.id}`}
              className="text-on-surface-variant transition-colors hover:text-cyan"
            >
              {node.name}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
}
